/*
 * @Author: Ryan
 * @Date: 2026-01-09
 * @Description: FAQ Section for Video Translator landing page
 */
import React, { useState } from 'react';

import styles from './index.module.scss';

export interface FAQItem {
  question: string;
  answer: string;
}

export interface FAQSectionProps {
  /** 区块标题 */
  title: string;
  /** FAQ 列表 */
  faqs: FAQItem[];
}

interface FAQItemComponentProps {
  item: FAQItem;
  index: number;
  isExpanded: boolean;
  onClick: () => void;
}

// 加号图标
const PlusIcon = () => (
  <svg className={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 4v16m8-8H4"
    />
  </svg>
);

// 减号图标
const MinusIcon = () => (
  <svg className={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
  </svg>
);

const FAQItemComponent: React.FC<FAQItemComponentProps> = ({
  item,
  isExpanded,
  onClick,
}) => {
  return (
    <div
      className={`${styles.faqItem} ${isExpanded ? styles.expanded : ''}`}
      onClick={onClick}
    >
      <div className={styles.faqContent}>
        {/* 问题行 */}
        <div className={styles.questionRow}>
          <h3 className={styles.question}>{item.question}</h3>
          <div className={styles.iconWrapper}>
            {isExpanded ? <MinusIcon /> : <PlusIcon />}
          </div>
        </div>

        {/* 答案 */}
        <div className={`${styles.answerWrapper} ${isExpanded ? styles.show : ''}`}>
          <p className={styles.answer}>{item.answer}</p>
        </div>
      </div>
    </div>
  );
};

const FAQSection: React.FC<FAQSectionProps> = ({ title, faqs }) => {
  // 默认展开第一个
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set([0]));

  const handleItemClick = (index: number) => {
    setExpandedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  return (
    <section className={styles.section}>
      <div className={styles.container}>
        {/* 标题 */}
        <h2 className={styles.title}>{title}</h2>

        {/* FAQ 列表 */}
        <div className={styles.faqList}>
          {faqs.map((item, index) => (
            <FAQItemComponent
              key={index}
              item={item}
              index={index}
              isExpanded={expandedIds.has(index)}
              onClick={() => handleItemClick(index)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
